import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface Stats {
  totalRecipes: number;
  publishedRecipes: number;
  unpublishedRecipes: number;
  totalUsers: number;
  totalAdmins: number;
  totalCategories: number;
  totalSubcategories: number;
  activeCategories: number;
  recentRecipes: number;
  recentUsers: number;
}

interface OrderStats {
  totalOrders: number;
  pendingOrders: number;
  confirmedOrders: number;
  deliveredOrders: number;
  rejectedOrders: number;
  totalRevenue: number;
  transactions: {
    total: number;
    completed: number;
    pending: number;
    failed: number;
    revenue: number;
  };
}

const COLORS = ['#2563eb', '#059669', '#7c3aed', '#dc2626', '#ea580c', '#0891b2', '#ca8a04', '#db2777'];

const CHART_MARGIN = { top: 16, right: 16, left: 16, bottom: 80 };

export default function StatsCards() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [stats, setStats] = useState<Stats | null>(null);
  const [orderStats, setOrderStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [statsResponse, ordersResponse] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/orders/stats').catch(() => ({ data: null })),
      ]);
      setStats(statsResponse.data.data);
      setOrderStats(ordersResponse?.data ?? null);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('stats.loading')}</p>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center py-8 text-red-600">{t('stats.failedToLoad')}</div>;
  }

  const recipeData = [
    { name: t('stats.published'), value: stats.publishedRecipes },
    { name: t('stats.unpublished'), value: stats.unpublishedRecipes },
  ];

  const userData = [
    { name: t('stats.regularUsers'), value: stats.totalUsers - stats.totalAdmins },
    { name: t('stats.admins'), value: stats.totalAdmins },
  ];

  const categoryData = [
    { name: t('stats.totalCategories'), value: stats.totalCategories },
    { name: t('stats.subcategories'), value: stats.totalSubcategories },
  ];

  const activityData = [
    { name: `${t('stats.recentRecipes')} (7d)`, value: stats.recentRecipes },
    { name: `${t('stats.recentUsers')} (7d)`, value: stats.recentUsers },
  ];

  const orderPieData = [
    { name: t('stats.pending'), value: orderStats?.pendingOrders ?? 0, fill: '#eab308' },
    { name: t('stats.confirmed'), value: orderStats?.confirmedOrders ?? 0, fill: '#2563eb' },
    { name: t('stats.delivered'), value: orderStats?.deliveredOrders ?? 0, fill: '#ea580c' },
    { name: t('stats.rejected'), value: orderStats?.rejectedOrders ?? 0, fill: '#dc2626' },
  ];

  return (
    <div className={`space-y-6 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-primary-orange rtl:border-l-0 rtl:border-r-4 rtl:border-primary-orange">
          <div className="text-gray-600 text-sm font-medium">{t('stats.totalRecipes')}</div>
          <div className="text-3xl font-bold text-black mt-2">{stats.totalRecipes}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500 rtl:border-l-0 rtl:border-r-4 rtl:border-green-500">
          <div className="text-gray-600 text-sm font-medium">{t('stats.published')}</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{stats.publishedRecipes}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500 rtl:border-l-0 rtl:border-r-4 rtl:border-yellow-500">
          <div className="text-gray-600 text-sm font-medium">{t('stats.unpublished')}</div>
          <div className="text-3xl font-bold text-yellow-600 mt-2">{stats.unpublishedRecipes}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500 rtl:border-l-0 rtl:border-r-4 rtl:border-blue-500">
          <div className="text-gray-600 text-sm font-medium">{t('stats.totalUsers')}</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{stats.totalUsers}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500 rtl:border-l-0 rtl:border-r-4 rtl:border-purple-500">
          <div className="text-gray-600 text-sm font-medium">{t('stats.admins')}</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">{stats.totalAdmins}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-medium">{t('stats.totalCategories')}</div>
          <div className="text-2xl font-bold text-black mt-2">{stats.totalCategories} {t('stats.mainCategories')}</div>
          <div className="text-lg text-gray-500 mt-1">{stats.totalSubcategories} {t('stats.subcategories')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-medium">{t('stats.activeCategories')}</div>
          <div className="text-2xl font-bold text-primary-orange mt-2">{stats.activeCategories}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-medium">{t('stats.recentActivity')}</div>
          <div className="text-2xl font-bold text-black mt-2">{stats.recentRecipes} {t('stats.recipesCount')}</div>
          <div className="text-lg text-gray-500 mt-1">{stats.recentUsers} {t('stats.newUsers')}</div>
        </div>
      </div>

      {/* Order Statistics */}
      {orderStats && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('stats.orderStatistics')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-orange">{orderStats.totalOrders}</div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.totalOrders')}</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{orderStats.pendingOrders}</div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.pending')}</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{orderStats.deliveredOrders}</div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.delivered')}</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{orderStats.rejectedOrders}</div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.rejected')}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-xl font-bold text-blue-600">
                {orderStats.totalRevenue.toFixed(2)} EGP
              </div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.totalRevenue')}</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-xl font-bold text-purple-600">
                {orderStats.transactions.completed}
              </div>
              <div className="text-sm text-gray-600 mt-1">{t('stats.completedTransactions')}</div>
            </div>
          </div>
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-700 mb-4">{t('stats.orderStatusDistribution')}</h4>
            <div dir="ltr" className="min-h-[340px]">
              <ResponsiveContainer width="100%" height={340}>
                <PieChart margin={{ top: 48, right: 16, left: 16, bottom: 80 }}>
                  <Pie
                    data={orderPieData}
                    cx="50%"
                    cy="48%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => (percent >= 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : '')}
                    labelLine={false}
                  >
                    {orderPieData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={_entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name: string, props?: { payload?: { value: number }; percent?: number }) => [`${value} (${((props?.percent ?? 0) * 100).toFixed(0)}%)`, name]} />
                  <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: 24 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Charts - force LTR and margins so labels/legend don't overlap in RTL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('stats.recipeStatus')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart margin={CHART_MARGIN}>
                <Pie
                  data={recipeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {recipeData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('stats.userDistribution')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart margin={CHART_MARGIN}>
                <Pie
                  data={userData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('stats.categoryDistribution')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                <Bar dataKey="value" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-black mb-4">{t('stats.recentActivityChart')}</h3>
          <div dir="ltr" className="min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" />
                <Bar dataKey="value" fill="#059669" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
